/* Movatic WMS — Coletor | Login-por-scan (gate de presença, ADR-0012-adenda F4) | CesarFilho 932048016 */
// Após o JWT, o operador cai aqui e NÃO passa às tarefas sem picar a etiqueta do PDA (D4).
// Pica o code do coletor -> POST /sessions/login/ {device_code} (o backend B1 resolve por code)
// -> sessão aberta (is_available_for_dispatch=True) -> entra no fluxo. Login idempotente (mesmo
// device = no-op; outro = PDA_SWITCH, o backend fecha a anterior silenciosamente).
import { useState } from 'react'
import { Camera, ScanLine } from 'lucide-react'
import CameraScanner from './CameraScanner'
import { useKeyboardScanner } from '../hooks/useKeyboardScanner'
import { operatorMessage } from '../lib/operatorMessage'
import { sanitizeScan } from '@/lib/scan'
import { useAuthStore } from '@/store/authStore'

function buildAuthHeaders(): HeadersInit {
  const state =
    (
      useAuthStore as unknown as { getState?: () => { accessToken?: string } }
    ).getState?.() ?? {}
  const token = state.accessToken
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

export default function LoginScanGate({
  onSuccess,
}: {
  onSuccess: () => void
}) {
  const [cam, setCam] = useState(false)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  // Zebra/teclado sempre à escuta (o operador pode picar sem abrir a câmara).
  useKeyboardScanner((code) => submit(code), { preventDefault: true })

  async function submit(raw: string) {
    const code = sanitizeScan(raw)
    if (!code || busy) return
    setBusy(true)
    setMsg(null)
    try {
      const res = await fetch('/api/workforce/sessions/login/', {
        method: 'POST',
        headers: buildAuthHeaders(),
        body: JSON.stringify({ device_code: code }),
      })
      if (res.ok) {
        // Sessão aberta (ou trocada por PDA_SWITCH, que o backend trata em silêncio).
        onSuccess()
        return
      }

      // ADR-0058 F1 — erro: distinguir por status; nunca inventar texto quando o backend
      // mandou um `detail`. O corpo pode não ser JSON (ex.: 401 do proxy) -> try/catch.
      let detail = ''
      try {
        const body = await res.json()
        detail = typeof body?.detail === 'string' ? body.detail : ''
      } catch {
        detail = ''
      }

      if (res.status === 401) {
        setMsg('A sessão expirou. Volta a entrar.')
      } else if (res.status === 403) {
        setMsg('Esta conta não tem perfil de operador.')
      } else {
        // 400 (e outros): o `detail` do backend, TRADUZIDO (D3). Fallback à frase atual se vier vazio.
        setMsg(
          detail
            ? operatorMessage(detail)
            : 'Coletor não reconhecido — pica outra etiqueta.',
        )
      }
    } catch {
      // fetch rejeitado -> sem ligação ao servidor.
      setMsg('Sem ligação ao servidor.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="coletor-root min-h-screen flex flex-col items-center justify-center p-6 text-center">
      <ScanLine size={48} className="mb-3 opacity-80" />
      <h1 className="text-xl font-semibold">Inicia a tua sessão</h1>
      <p className="mt-2 opacity-80">
        Pica a etiqueta do coletor (PDA) para começar.
      </p>

      {cam ? (
        <div className="mt-5 w-full max-w-xs">
          <CameraScanner
            onRead={(code) => submit(code)}
            className="w-full rounded-lg"
          />
          <button
            className="btn mt-2"
            onClick={() => setCam(false)}
            disabled={busy}
          >
            Cancelar câmara
          </button>
        </div>
      ) : (
        <button
          className="btn mt-5 inline-flex items-center gap-2"
          onClick={() => setCam(true)}
          disabled={busy}
        >
          <Camera size={18} /> Abrir câmara
        </button>
      )}

      {busy && <div className="mt-3 opacity-70">A iniciar sessão…</div>}
      {msg && <div className="mt-3 text-sm opacity-90">{msg}</div>}
    </div>
  )
}
